import videojs from 'video.js';
import { OverflowMenuButton } from './controlbar-menu/overflow-menu-button.js';
import { NotificationItem } from './notifications/notification-item.js';
import NotificationsIcon from '../../assets/images/notifications_white_24dp.svg';
import InfoIcon from '../../assets/images/info-white-24dp.svg';
import './notifications.css';

/**
 * @typedef {Object} NotificationsMenuButtonData
 * @property {string} [id]
 * @property {string} text The text to display.
 * @property {string} [description] Descriptive text for the notification.
 * @property {function} callback
 */

const _menuButton = new WeakMap();
const _notificationContainer = new WeakMap();
let localize;

/**
 * The Notifications module.
 * @member {NotificationsPlugin} notifications
 * @memberof MediasitePlayer#
 */

/**
 * Enables displaying custom notifications in the player.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class NotificationsPlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        localize = player.localize?.bind(player);
        super(player, options);

        // Create controlbar button + menu
        const menuButton = new OverflowMenuButton(player, {
            name: 'NotificationsMenuButton',
            icon: NotificationsIcon
        });
        menuButton.addClass('mediasite-player__controlbar-notifications-button');
        menuButton.controlText(localize('Notifications'));
        _menuButton.set(this, menuButton);

        const Component = videojs.getComponent('Component');
        const notificationContainer = new Component(player);
        notificationContainer.el().setAttribute('role', 'list');
        notificationContainer.addClass('mediasite-player__notifications-container');
        _notificationContainer.set(this, notificationContainer);

        player.ready(() => {
            const overflowButtonIndex = player.getChild('ControlBar').children().indexOf(
                player.getDescendant('ControlBar', 'OverflowMenuButton')
            );
            player.getChild('ControlBar').addChild(menuButton, null, overflowButtonIndex);
            player.layout().addChild(notificationContainer);
        });
    }

    /**
     * Adds a notification to the notification queue and shows it.
     * @param {string} title
     * @param {Object} options
     * @param {function({ type: 'invoke'|'dismiss' })} [options.callback]
     * @param {string} [options.description] Descriptive text to show on a second line.
     * @param {string} [options.icon] URL-encoded image to use as the icon for this notification.
     * @param {number} [options.timeout] How long to display the notification, in milliseconds. May be Infinity. Defaults to 5000ms.
     * @param {string} [options.id] An optional ID for the notification in case you want to remove it later. Defaults to an automatically generated unique ID.
     * @returns {string} The ID of the notification.
     * @example
     * const notificationId = player.notifications().add('This is a notification', {
     *     timeout: Infinity,
     *     description: 'Some extra text'
     * });
     */
    add(title, options = {}) {
        if (typeof options === 'function') {
            options = {
                callback: options
            }
        }

        const { callback = () => { }, description,  icon = InfoIcon, timeout } = options;

        const notificationId = _menuButton.get(this).addMenuItem(title, {
            callback: (...args) => {
                const [ event ] = args;
                this.remove(notificationId);
                if (event.type === 'invoke') {
                    return callback(...args);
                }
            },
            dismissable: true,
            icon
        });

        const notificationContainer = _notificationContainer.get(this);
        const notificationItem = new NotificationItem(this.player, {
            title,
            id: notificationId,
            description: description,
            timeout: timeout,
            icon: icon
        });
        notificationContainer.addChild(notificationItem);

        const existingChildren = notificationContainer.children();
        const itemIndex = existingChildren.indexOf(notificationItem);
        for (let i = 0; i < itemIndex; i++) {
            existingChildren[i].animateUp();
        }

        notificationItem.one(['dismiss', 'timeout'], () => this.removeDisplayItem(notificationId));
        notificationItem.one('callback', () => {
            this.remove(notificationId)
            setTimeout(() => {
                callback();
            }, 0);
        });

        this.log(title, "notification added.")

        return notificationId;
    }

    /**
     * Removes the notification with the given ID.
     * @param {string} id
     * @example
     * player.notifications.remove('notificationId');
     */
    remove(id) {
        setTimeout(() => {
            _menuButton.get(this).removeMenuItem(id);
        }, 0);
        this.removeDisplayItem(id);
    }

    /**
     * Removes the banner associated with the notification with the given ID, but does not remove it from the controlbar menu.
     * @param {string} id
     * @example
     * player.notifications.removeDisplayItem('notificationId');
     */
    removeDisplayItem(id) {
        const notificationContainer = _notificationContainer.get(this);
        const notificationItem = notificationContainer.children().find(child => child.id() === id);
        
        if (notificationItem) {
            const existingChildren = notificationContainer.children();
            const itemIndex = existingChildren.indexOf(notificationItem);
            for (let i = 0; i < itemIndex; i++) {
                existingChildren[i].animateDown();
            }

            notificationItem.animateOut().then(() => {
                notificationContainer.removeChild(notificationItem);
            });
        }
    }

    /**
     * Returns a boolean based upon if the provided id is actually being displayed as a notification
     * @param {string} id
     */
    hasDisplayItem(id) {
        return _menuButton.get(this).hasItem(id);
    }

    setupMuteNotification() {
        this.player.one('loadeddata', () => {
            const notificationId = this.add(localize('Video is muted'), {
                description: localize('Click here to unmute'),
                callback: () => this.player.muted(false),
                timeout: Infinity
            });
            this.player.one('volumechange', () => {
                this.remove(notificationId);
            });
        });
    }

    dispose() {
        const menuButton = _menuButton.get(this);
        menuButton.dispose();

        const controlbar = this.player.getChild('ControlBar');
        controlbar.removeChild(menuButton);

        this.player.layout().removeChild(_notificationContainer.get(this));

        super.dispose();
    }
}

export default NotificationsPlugin;
